import { Modal, Form } from "react-bootstrap";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Alphabet, At, Telephone } from "react-bootstrap-icons";
import { Button } from "./Controls/Button";
import { Input } from "./Controls/Input";
import { PhoneInput } from "./Controls/PhoneInput";
import { HeaderText } from "./Controls/HeaderText";
import { SubscriptionParker } from "./Data/ApiTypes";
import { createApiService } from "./shared/apiConfig";
import { GroupParkerApi } from "./shared/api/axios-client";

const groupParkerApi = createApiService(GroupParkerApi);

interface Props {
    show: boolean;
    onHide: () => void;
    subscriptionId: number;
    parker: SubscriptionParker;
    onSuccess: (updated: SubscriptionParker) => void;
}

interface FormValues {
    name: string;
    emailAddress: string;
    phoneNumber: string;
}

export function GroupParkerQuickEditModal({ show, onHide, subscriptionId, parker, onSuccess }: Props) {
    const defaults = useMemo<FormValues>(() => ({
        name: parker.parker.name,
        emailAddress: parker.parker.isAutoGeneratedEmail ? "" : (parker.parker.emailAddress ?? ""),
        phoneNumber: parker.parker.phoneNumber ?? "",
    }), [parker]);

    const { register, handleSubmit, reset, watch, formState: { errors, isSubmitting } } = useForm<FormValues>({
        defaultValues: defaults,
    });

    const values = watch();
    const isDirty =
        (values.name ?? "") !== defaults.name ||
        (values.emailAddress ?? "") !== defaults.emailAddress ||
        (values.phoneNumber ?? "") !== defaults.phoneNumber;

    useEffect(() => {
        if (show) {
            reset(defaults);
        }
    }, [show, defaults, reset]);

    const onSubmit = async (data: FormValues) => {
        try {
            const resp = await groupParkerApi.groupParkerUpdate({
                subscriptionId,
                parkerId: parker.parker.parkerId,
                serverRequestsGroupParkerUpdateRequest: {
                    name: data.name.trim(),
                    emailAddress: data.emailAddress.trim(),
                    phoneNumber: data.phoneNumber.trim(),
                },
            });
            onSuccess(resp.data.parker as unknown as SubscriptionParker);
            toast.success("Parker updated successfully");
            onHide();
        } catch (err: unknown) {
            // ASP.NET BadRequest(string) → response.data is the message string
            const data = (err as { response?: { data?: unknown } })?.response?.data;
            const message =
                typeof data === "string" ? data
                : typeof (data as { message?: unknown })?.message === "string"
                    ? (data as { message: string }).message
                    : "Failed to update parker. Please try again.";
            toast.error(message);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0 pb-0">
                <HeaderText>Edit Parker</HeaderText>
            </Modal.Header>
            <Modal.Body className="pt-2">
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Input
                        type="text"
                        icon={<Alphabet />}
                        label="Name"
                        placeholder="Full name"
                        {...register("name", { required: "Name is required" })}
                        error={errors.name}
                    />
                    <Input
                        type="email"
                        icon={<At />}
                        label="Email address (optional)"
                        placeholder="Email address"
                        {...register("emailAddress", {
                            pattern: {
                                value: /^$|^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: "Enter a valid email",
                            },
                        })}
                        error={errors.emailAddress}
                    />
                    <PhoneInput
                        icon={<Telephone />}
                        label="Phone Number (Optional)"
                        placeholder="555-555-5555"
                        value={values.phoneNumber ?? ""}
                        {...register("phoneNumber", {
                            pattern: {
                                value: /^$|^\d{3}-\d{3}-\d{4}$/,
                                message: "Phone number must be in format ###-###-####",
                            },
                        })}
                        error={errors.phoneNumber}
                    />
                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <Button className="px-4" onClick={onHide} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="btn-main px-4"
                            loading={isSubmitting}
                            loadingText="Saving…"
                            disabled={!isDirty || isSubmitting}
                        >
                            Save
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}
